"use client";

import { type ReactNode, useMemo, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	EmptyState,
	GlassButton,
	GlassCard,
	ListRow,
	NavigationBar,
	Skeleton,
	Toast,
} from "@/components/design-system/ArtistDesignSystem";
import { useArtistRuntimeConfiguration } from "@/components/runtime/ArtistRuntimeConfigurationProvider";
import type { ArtistAuthProviderKey } from "@/domain/auth/AuthProviderConfiguration";
import Link from "@/infrastructure/router/Link";
import { useRouter } from "@/infrastructure/router/navigation";

type ConnectedProviderKind = "email" | "phone" | "social";

interface ConnectedAccountProvider {
	readonly id: string;
	readonly title: string;
	readonly kind: ConnectedProviderKind;
	readonly glyph: ReactNode;
	readonly connectedSubtitle?: (session: {
		readonly email: string;
		readonly phone: string;
	}) => string;
	readonly disconnectedSubtitle: string;
}

const RECOVERY_PROVIDERS: readonly ConnectedAccountProvider[] = [
	{
		id: "email",
		title: "邮箱",
		kind: "email",
		glyph: <ArtistIcon name="mail" />,
		connectedSubtitle: ({ email }) => maskedEmail(email ?? ""),
		disconnectedSubtitle: "在账户设置中添加邮箱以恢复资料。",
	},
	{
		id: "phone",
		title: "手机号",
		kind: "phone",
		glyph: <ArtistIcon name="phone" />,
		connectedSubtitle: ({ phone }) => maskedPhone(phone ?? ""),
		disconnectedSubtitle: "仅显示历史已连接号码。",
	},
];

const SOCIAL_PROVIDER_TITLES: Readonly<Record<ArtistAuthProviderKey, string>> =
	{
		apple: "Apple",
		douyin: "抖音",
		facebook: "Facebook",
		google: "Google",
		"linux-do": "Linux Do",
		qq: "QQ",
		watcha: "观猹",
		wechat: "微信",
		x: "X",
	};

function socialProvider(key: ArtistAuthProviderKey): ConnectedAccountProvider {
	return {
		id: key,
		title: SOCIAL_PROVIDER_TITLES[key],
		kind: "social",
		glyph: <SocialProviderGlyph provider={key} />,
		disconnectedSubtitle: "未连接",
	};
}

function maskedEmail(email: string): string {
	if (email.length === 0) return "已连接";
	const parts = email.split("@");
	if (parts.length !== 2 || parts[0]?.length === undefined)
		return maskedIdentifier(email);
	const name = parts[0];
	const domain = parts[1];
	const visible = name.slice(0, Math.min(2, name.length));
	return `${visible}***@${domain}`;
}

function maskedPhone(phone: string): string {
	const digits = phone.replace(/\D/g, "");
	if (digits.length <= 6) return phone.length === 0 ? "已连接" : phone;
	const prefix = phone.startsWith("+")
		? `+${digits.slice(0, 2)}`
		: digits.slice(0, 2);
	return `${prefix}***${digits.slice(-4)}`;
}

function maskedIdentifier(value: string): string {
	if (value.length <= 8) return value;
	return `${value.slice(0, 4)}...${value.slice(-4)}`;
}

function SocialProviderGlyph({
	provider,
}: Readonly<{ provider: ArtistAuthProviderKey }>) {
	return (
		<img
			src={`/images/auth/providers/${provider}.png`}
			alt=""
			width={20}
			height={20}
			aria-hidden="true"
		/>
	);
}

type ConnectedAccountDialog =
	| { readonly kind: "disconnect"; readonly provider: ConnectedAccountProvider }
	| {
			readonly kind: "unavailable";
			readonly provider: ConnectedAccountProvider;
	  };

export function ConnectedAccountHub() {
	const auth = useArtistSession();
	const runtimeConfiguration = useArtistRuntimeConfiguration();
	const router = useRouter();
	const [dialog, setDialog] = useState<ConnectedAccountDialog | null>(null);

	const sessionSnapshot = useMemo(
		() => ({
			email:
				auth.session?.email.startsWith("+") === false ? auth.session.email : "",
			phone:
				auth.session?.email.startsWith("+") === true ? auth.session.email : "",
		}),
		[auth.session?.email],
	);

	if (auth.phase === "loading") {
		return (
			<section className="native-page account-connected-page" aria-busy="true">
				<NavigationBar title="已连接账户" backHref="/settings" />
				<Skeleton rows={4} />
			</section>
		);
	}

	if (auth.phase === "error") {
		return (
			<section className="native-page account-connected-page">
				<NavigationBar title="已连接账户" backHref="/settings" />
				<Toast tone="error">账户信息暂时无法载入。</Toast>
				<div className="account-connected__retry">
					<GlassButton kind="primary" onClick={auth.retryRestore}>
						重新载入
					</GlassButton>
				</div>
			</section>
		);
	}

	if (auth.phase === "guest" || auth.session === null) {
		return (
			<section className="native-page account-connected-page">
				<NavigationBar title="已连接账户" backHref="/settings" />
				<EmptyState
					title="登录后管理账户"
					message="连接邮箱或第三方账户，让你的资料在设备间保持一致。"
					symbol={<ArtistIcon name="link" size={42} />}
				/>
				<Link className="primary-action" href="/auth">
					登录
				</Link>
			</section>
		);
	}

	const connectedIdentifier = auth.session.email;
	const connectedProviderId = connectedIdentifier.startsWith("+")
		? "phone"
		: "email";
	const visibleProviders = [
		...RECOVERY_PROVIDERS.filter(
			(provider) => provider.id === connectedProviderId,
		),
		...runtimeConfiguration.connectedAccountProviderKeys.map(socialProvider),
	];

	function handleRowTap(provider: ConnectedAccountProvider): void {
		setDialog({
			kind: provider.id === connectedProviderId ? "disconnect" : "unavailable",
			provider,
		});
	}

	async function disconnectCurrentAccount(): Promise<void> {
		const signedOut = await auth.signOut();
		setDialog(null);
		if (signedOut) router.replace("/auth");
	}

	return (
		<section
			className="native-page account-connected-page"
			aria-label="已连接账户"
		>
			<NavigationBar title="已连接账户" backHref="/settings" />
			<p className="native-page-intro">当前可用于登录与恢复资料的账户。</p>

			<GlassCard className="connected-account-list">
				<ul className="account-connected__list" aria-label="已连接账户列表">
					{visibleProviders.map((provider, index) => {
						const isConnected = provider.id === connectedProviderId;
						const subtitle =
							isConnected && provider.connectedSubtitle
								? provider.connectedSubtitle(sessionSnapshot)
								: provider.disconnectedSubtitle;
						const trailing = isConnected ? (
							<span className="status-badge status-badge--connected">
								已连接
							</span>
						) : null;
						return (
							<li key={provider.id} className="account-connected__list-item">
								{index > 0 ? (
									<span
										className="account-connected__divider"
										aria-hidden="true"
									/>
								) : null}
								<ListRow
									title={provider.title}
									subtitle={subtitle}
									leading={
										<span className="account-connected__glyph">
											{provider.glyph}
										</span>
									}
									trailing={trailing}
									onClick={() => handleRowTap(provider)}
								/>
							</li>
						);
					})}
				</ul>
			</GlassCard>

			<GlassCard className="recovery-callout">
				<span className="recovery-callout__icon" aria-hidden="true">
					<ArtistIcon name="shield" size={16} />
				</span>
				<p className="recovery-callout__copy">
					账户连接使用加密会话，不会在此设备显示密码。
				</p>
			</GlassCard>

			<ConnectedAccountDialog
				dialog={dialog}
				onClose={() => setDialog(null)}
				onConfirmDisconnect={() => void disconnectCurrentAccount()}
			/>
		</section>
	);
}

interface ConnectedAccountDialogProps {
	readonly dialog: ConnectedAccountDialog | null;
	readonly onClose: () => void;
	readonly onConfirmDisconnect: () => void;
}

function ConnectedAccountDialog({
	dialog,
	onClose,
	onConfirmDisconnect,
}: Readonly<ConnectedAccountDialogProps>) {
	const open = dialog !== null;
	const title = dialog?.provider.title ?? "";
	return (
		<dialog
			className="account-connected-dialog"
			open={open}
			aria-labelledby="account-connected-dialog-title"
		>
			{dialog === null ? null : dialog.kind === "disconnect" ? (
				<div className="account-connected-dialog__body">
					<h2 id="account-connected-dialog-title">断开 {title} 连接？</h2>
					<p>断开后，此账户将无法用于登录 Artist，但仍可在登录页重新连接。</p>
					<div className="account-connected-dialog__actions">
						<GlassButton
							kind="destructive"
							type="button"
							onClick={onConfirmDisconnect}
						>
							断开连接
						</GlassButton>
						<GlassButton kind="secondary" type="button" onClick={onClose}>
							取消
						</GlassButton>
					</div>
				</div>
			) : (
				<div className="account-connected-dialog__body">
					<h2 id="account-connected-dialog-title">{title} 登录暂未接入</h2>
					<p>OAuth 适配器正在路上，敬请期待。当前请使用邮箱验证码登录。</p>
					<div className="account-connected-dialog__actions">
						<GlassButton kind="primary" type="button" onClick={onClose}>
							知道了
						</GlassButton>
					</div>
				</div>
			)}
		</dialog>
	);
}
